import {
  AutomationCreator,
  AutomationParentScope,
  AutomationStatus,
  EmailMessage,
  IAutomatedNotification,
  IAutomation,
  IAutomationResource,
  IAutomationScopeData,
  IBaseMarketingCampaignStepConversion,
  IGeneratedTask,
  IMarketingCampaign,
  IMarketingCampaignEntry,
  IMarketingCampaignStep,
  IMarketingCampaignStepConversion,
  isAutomatedNotification,
  isGeneratedTask,
  MarketingCampaignEntryCollection,
  MarketingCampaignStepConversionStatus,
  SMSMessage,
} from '@principle-theorem/principle-core/interfaces';
import {
  asReffable,
  AtLeast,
  collectionGroupQuery,
  DocumentReference,
  FieldValue,
  Firestore,
  IReffable,
  Timestamp,
  toNamedDocument,
  toTimestamp,
  where,
  WithRef,
} from '@principle-theorem/shared';
import { Event } from '../event/event';
import { AutomatedNotification } from '../notification/automated-notification';
import { Patient } from '../patient/patient';
import { GeneratedTask } from '../task/generated-task';
import { MarketingCampaignEntry } from './campaign-entry';
import { MarketingCampaignRun } from './campaign-run';
import { MarketingCampaignStep } from './campaign-step';

export type TranslateTriggerDateFn = (
  automation: IAutomation<IAutomatedNotification | IGeneratedTask>
) => Promise<Timestamp | undefined>;

export class MarketingCampaignStepConversion {
  static init<
    T extends Omit<
      IMarketingCampaignStepConversion,
      'deleted' | 'conversionUid'
    >,
  >(
    overrides: T &
      AtLeast<IBaseMarketingCampaignStepConversion, 'automationRef' | 'stepRef'>
  ): T & IBaseMarketingCampaignStepConversion {
    return {
      deleted: false,
      ...overrides,
    };
  }

  static async fromAutomation(
    automation: IReffable<IAutomation<IAutomationResource>>
  ): Promise<WithRef<IMarketingCampaignStepConversion> | undefined> {
    return Firestore.firstResult(
      collectionGroupQuery<IMarketingCampaignStepConversion>(
        MarketingCampaignEntryCollection.StepConversions,
        where('automationRef', '==', automation.ref)
      )
    );
  }

  static async entryFromAutomation(
    automation: WithRef<IAutomation<IAutomationResource>>
  ): Promise<WithRef<IMarketingCampaignEntry> | undefined> {
    if (automation.campaignEntryRef) {
      return Firestore.getDoc(automation.campaignEntryRef);
    }

    const stepConversion =
      await MarketingCampaignStepConversion.fromAutomation(automation);
    if (!stepConversion) {
      return;
    }

    const entryRef = MarketingCampaignStepConversion.entryRef(stepConversion);
    return Firestore.getDoc(entryRef);
  }

  static async recordCost(
    stepConversionRef: DocumentReference<IMarketingCampaignStepConversion>,
    cost: number
  ): Promise<void> {
    await Firestore.patchDoc(stepConversionRef, {
      estimatedCostRemaining: 0,
      incurredCost: cost,
    });
  }

  static async fromSMSMessage(
    smsMessage: WithRef<SMSMessage>
  ): Promise<WithRef<IMarketingCampaignStepConversion> | undefined> {
    if (!smsMessage.automationRef) {
      return;
    }
    return MarketingCampaignStepConversion.fromAutomation(
      asReffable(smsMessage.automationRef)
    );
  }

  static async fromEmailMessage(
    email: WithRef<EmailMessage>
  ): Promise<WithRef<IMarketingCampaignStepConversion> | undefined> {
    if (!email.automationRef) {
      return;
    }
    return MarketingCampaignStepConversion.fromAutomation(
      asReffable(email.automationRef)
    );
  }

  static getCampaignRef(
    conversion: WithRef<IMarketingCampaignStepConversion>
  ): DocumentReference<IMarketingCampaign> {
    return MarketingCampaignRun.campaignRef(
      asReffable(conversion.campaignRunRef)
    );
  }

  static entryRef(
    conversion: IReffable<IMarketingCampaignStepConversion>
  ): DocumentReference<IMarketingCampaignEntry> {
    return Firestore.getParentDocRef<IMarketingCampaignEntry>(conversion.ref);
  }

  static async queueAutomation(
    stepConversion: WithRef<IMarketingCampaignStepConversion>,
    translateTriggerDateFn?: TranslateTriggerDateFn
  ): Promise<DocumentReference<IAutomation<IAutomationResource>> | undefined> {
    return Firestore.runTransaction(async (transaction) => {
      const entryRef = MarketingCampaignStepConversion.entryRef(stepConversion);
      const entry = await Firestore.getDoc(entryRef);

      const campaign = await MarketingCampaignEntry.campaign(entry);
      const campaignStep = await Firestore.getDoc(stepConversion.stepRef);

      const automation =
        await MarketingCampaignStepConversion.generateForCampaign(
          campaign,
          campaignStep,
          entry,
          entry.scopeData
        );

      if (!automation) {
        // eslint-disable-next-line no-console
        console.error('No automation generated for step', {
          campaignStep,
          campaign,
          entry: entryRef,
        });
        return;
      }

      if (translateTriggerDateFn) {
        automation.triggerDate = await translateTriggerDateFn(automation);
      }

      const automationRef = await Firestore.addDoc(
        Patient.automationCol(entry.patient),
        automation,
        undefined,
        transaction
      );
      await Firestore.patchDoc(
        stepConversion.ref,
        { automationRef },
        transaction
      );

      return automationRef;
    });
  }

  static async cancelAutomation(
    stepConversion: WithRef<IMarketingCampaignStepConversion>,
    reason?: string
  ): Promise<void> {
    await Firestore.runTransaction(async (transaction) => {
      if (!stepConversion.automationRef) {
        return;
      }
      const automation = await Firestore.getDoc(
        stepConversion.automationRef,
        transaction
      );

      const skipStatuses = [
        AutomationStatus.Cancelled,
        AutomationStatus.Skipped,
        AutomationStatus.Completed,
      ];

      if (skipStatuses.includes(automation.status)) {
        return;
      }

      await Firestore.patchDoc(
        stepConversion.automationRef,
        {
          status: AutomationStatus.Cancelled,
          triggerDate: FieldValue.delete(),
          errorMessage: reason ?? FieldValue.delete(),
        },
        transaction
      );
    });
  }

  static shouldCancelAutomation(
    stepConversionBefore: WithRef<IMarketingCampaignStepConversion> | undefined,
    stepConversionAfter: WithRef<IMarketingCampaignStepConversion>
  ): boolean {
    if (stepConversionBefore?.status === stepConversionAfter.status) {
      return false;
    }

    const stepsThatShouldCancel = [
      MarketingCampaignStepConversionStatus.Skipped,
      MarketingCampaignStepConversionStatus.Cancelled,
      MarketingCampaignStepConversionStatus.ExceededCommunicationLimit,
      MarketingCampaignStepConversionStatus.Unenrolled,
      MarketingCampaignStepConversionStatus.Unsubscribed,
    ];
    return stepsThatShouldCancel.includes(stepConversionAfter.status);
  }

  static async generateForCampaign(
    campaign: WithRef<IMarketingCampaign>,
    campaignStep: WithRef<IMarketingCampaignStep>,
    campaignEntry: WithRef<IMarketingCampaignEntry>,
    scopeData: IAutomationScopeData
  ): Promise<IAutomation<IAutomatedNotification | IGeneratedTask> | undefined> {
    if (!MarketingCampaignStep.isActive(campaignStep)) {
      return;
    }

    const brand = await Firestore.getDoc(campaign.brandRef);
    const practice = await Firestore.getDoc(campaign.practiceRef);

    const creator = {
      ...toNamedDocument(campaign),
      type: AutomationCreator.MarketingCampaign,
    };

    const lastStepCompletedAt =
      (await MarketingCampaignEntry.getMostRecentStepAt(campaignEntry)) ??
      toTimestamp();

    if (isAutomatedNotification(campaignStep.config)) {
      const automation = await AutomatedNotification.toAutomation(
        campaignStep.config,
        creator,
        Event.init({
          from: lastStepCompletedAt,
          to: lastStepCompletedAt,
          practice,
        }),
        brand.ref,
        {
          parentScope: AutomationParentScope.Patient,
          ...scopeData,
        }
      );

      return {
        ...automation,
        triggerAfterDate: lastStepCompletedAt,
        campaignEntryRef: campaignEntry.ref,
      };
    }

    if (isGeneratedTask(campaignStep.config)) {
      const automation = await GeneratedTask.toAutomation(
        campaignStep.config,
        creator,
        Event.init({
          from: lastStepCompletedAt,
          to: lastStepCompletedAt,
          practice,
        }),
        brand.ref,
        {
          parentScope: AutomationParentScope.Patient,
          ...scopeData,
        }
      );

      return {
        ...automation,
        triggerAfterDate: lastStepCompletedAt,
        campaignEntryRef: campaignEntry.ref,
      };
    }
  }
}
